import {Authenticated} from '@management-ui/core';
import {
  Archive as ArchiveIcon,
  Assignment,
  AssignmentReturned,
  Cached,
  Dashboard as DashboardIcon,
  Inbox as InboxIcon,
  PointOfSale,
  RecentActors,
  Security,
  Storage
} from '@mui/icons-material';
import {Box} from '@mui/material';
import * as React from 'react';
import {useCallback, useContext, useEffect, useRef} from 'react';
import {useDispatch} from 'react-redux';
import {Redirect, Route, Switch} from 'react-router-dom';
import logo from '../../assets/revoplas.svg';
import {ServiceContext} from '../../components/Services';
import withInboxCount from '../../components/withInboxCount';
import {usePermissions} from '../../hooks';
import routes from '../../routes';
import {logout} from '../../store/actions/auth';
import Archive from './screens/Archive';
import Contact from './screens/Contact';
import Contacts from './screens/Contacts';
import Dashboard from './screens/Dashboard';
import Inbox from './screens/Inbox';
import InboxMessage from './screens/InboxMessage';
import Management from './screens/Management';
import Material from './screens/Material';
import Materials from './screens/Materials';
import PackingList from './screens/PackingList';
import PurchaseOrder from './screens/PurchaseOrder';
import PurchaseOrders from './screens/PurchaseOrders';
import SalesOrder from './screens/SalesOrder';
import SalesOrders from './screens/SalesOrders';
import User from './screens/User';
import Users from './screens/Users';
import Tasks from './screens/Tasks';
import Task from './screens/Task';
import TaskManager from '../../components/TaskManager';

const Admin = (props) => {
  const services = useContext(ServiceContext);
  const {user, userCan} = usePermissions();
  const dispatch = useDispatch();

  const userCanOrRedirect = useCallback((permission, can = null) => {
    const hasPermission = userCan(permission, can);
    return hasPermission ?? <Redirect to={`${routes.dashboard}`}/>;
  }, [userCan]);

  const firebaseInitialised = useRef(false);

  useEffect(() => {
    if (userCan(['manage_inbox', 'complete_tasks'], true, false)) {
      if (!firebaseInitialised.current) {
        firebaseInitialised.current = true;
        services.auth.firebaseToken().then(token => {
          services.firebase.signIn(token).then(() => null).catch(() => null);
        }).catch(() => null);
      }
    }
  }, [services, userCan]);

  useEffect(() => () => {
    firebaseInitialised.current = false;
  }, []);

  return (
    <Authenticated
      title="Revoplas"
      logo={(
        <Box marginY={1} display="flex" alignItems="center" justifyContent="center">
          <img
            src={logo}
            alt="Revoplas"
            style={{width: '60%'}}/>
        </Box>
      )}
      userName={user ? user.first_name : ''}
      headerContent={userCan(['complete_tasks'], <TaskManager/>, null)}
      nav={[
        {title: 'Dashboard', route: routes.dashboard, icon: <DashboardIcon/>},
        ...userCan(['manage_contacts'], [
          {title: 'Contacts', route: routes.contacts.index, icon: <RecentActors/>}
        ], []),
        ...userCan(['manage_inbox'], [
          {
            title: props['inboxCount'] ? `Inbox (${props['inboxCount']})` : 'Inbox',
            route: routes.inbox.index,
            icon: <InboxIcon/>,
            bold: props['inboxCount'] > 0
          }
        ], []),
        ...userCan(['complete_tasks'], [
          {
            title: 'Tasks',
            route: routes.tasks.index,
            icon: <Assignment/>
          }
        ], []),
        ...userCan(['manage_purchase_orders'], [
          {title: 'Purchase Orders', route: routes.purchaseOrders.index, icon: <AssignmentReturned/>}
        ], []),
        ...userCan(['manage_sales_orders'], [
          {title: 'Sales Orders', route: routes.salesOrders.index, icon: <PointOfSale/>}
        ], []),
        ...userCan(['manage_materials'], [
          {title: 'Materials', route: routes.materials.index, icon: <Cached/>}
        ], []),
        ...userCan(['manage_users'], [
          {title: 'Users', route: routes.users.index, icon: <Security/>}
        ], []),
        ...userCan(['change_settings', 'manage_access_keys', 'manage_attributes'], [{
          title: 'Management',
          route: routes.management,
          icon: <Storage/>
        }], []),
        ...userCan(['manage_contacts'], [
          {title: 'Archive', route: routes.archive.index, icon: <ArchiveIcon/>}
        ], []),
      ]}
      onLogout={() => dispatch(logout())}
    >
      <Switch>

        <Route
          exact
          path={routes.dashboard}
          render={routeProps => <Dashboard {...props} {...routeProps} />}/>

        <Route
          exact
          path={routes.contacts.index}
          render={routeProps => userCanOrRedirect(['manage_contacts'], <Contacts {...props} {...routeProps} />)}/>
        <Route
          exact
          path={routes.contacts.detail}
          render={routeProps => userCanOrRedirect(['manage_contacts'], <Contact {...props} {...routeProps} />)}/>
        <Route
          exact
          path={routes.contacts.tab}
          render={routeProps => userCanOrRedirect(['manage_contacts'], <Contact {...props} {...routeProps} />)}/>
        <Route
          exact
          path={routes.archive.contact.detail}
          render={routeProps => userCanOrRedirect(
            ['manage_contacts'], <Contact {...props} {...routeProps} archive={true}/>
          )}/>
        <Route
          exact
          path={routes.archive.contact.tab}
          render={routeProps => userCanOrRedirect(
            ['manage_contacts'], <Contact {...props} {...routeProps} archive={true}/>
          )}/>

        <Route
          exact
          path={routes.inbox.index}
          render={routeProps => userCanOrRedirect(['manage_inbox'], <Inbox {...props} {...routeProps} />)}/>
        <Route
          exact
          path={routes.inbox.detail}
          render={routeProps => userCanOrRedirect(['manage_inbox'], <InboxMessage {...props} {...routeProps} />)}/>
        <Route
          exact
          path={routes.inbox.tab}
          render={routeProps => userCanOrRedirect(['manage_inbox'], <InboxMessage {...props} {...routeProps} />)}/>

        <Route
          exact
          path={routes.tasks.index}
          render={routeProps => userCanOrRedirect(['complete_tasks'], <Tasks {...props} {...routeProps} />)}/>
        <Route
          exact
          path={routes.tasks.detail}
          render={routeProps => userCanOrRedirect(['complete_tasks'], <Task {...props} {...routeProps} />)}/>
        <Route
          exact
          path={routes.tasks.tab}
          render={routeProps => userCanOrRedirect(['complete_tasks'], <Task {...props} {...routeProps} />)}/>
        <Route
          exact
          path={routes.archive.task.detail}
          render={routeProps => userCanOrRedirect(
            ['complete_tasks'], <Task {...props} {...routeProps} archive={true}/>
          )}/>
        <Route
          exact
          path={routes.archive.task.tab}
          render={routeProps => userCanOrRedirect(
            ['complete_tasks'], <Task {...props} {...routeProps} archive={true}/>
          )}/>

        <Route
          exact
          path={routes.purchaseOrders.index}
          render={routeProps => userCanOrRedirect(['manage_purchase_orders'],
            <PurchaseOrders {...props} {...routeProps} />)}/>
        <Route
          exact
          path={routes.purchaseOrders.detail}
          render={routeProps => userCanOrRedirect(['manage_purchase_orders'],
            <PurchaseOrder {...props} {...routeProps} />)}/>
        <Route
          exact
          path={routes.purchaseOrders.tab}
          render={routeProps => userCanOrRedirect(['manage_purchase_orders'],
            <PurchaseOrder {...props} {...routeProps} />)}/>
        <Route
          exact
          path={routes.archive.purchaseOrder.detail}
          render={routeProps => userCanOrRedirect(
            ['manage_purchase_orders'], <PurchaseOrder {...props} {...routeProps} archive={true}/>
          )}/>
        <Route
          exact
          path={routes.archive.purchaseOrder.tab}
          render={routeProps => userCanOrRedirect(
            ['manage_purchase_orders'], <PurchaseOrder {...props} {...routeProps} archive={true}/>
          )}/>

        <Route
          exact
          path={routes.salesOrders.index}
          render={routeProps => userCanOrRedirect(['manage_sales_orders'],
            <SalesOrders {...props} {...routeProps} />)}/>
        <Route
          exact
          path={routes.salesOrders.detail}
          render={routeProps => userCanOrRedirect(['manage_sales_orders'],
            <SalesOrder {...props} {...routeProps} />)}/>
        <Route
          exact
          path={routes.salesOrders.packingList.detail}
          render={routeProps => userCanOrRedirect(['manage_sales_orders'],
            <PackingList {...props} {...routeProps} />)}/>
        <Route
          exact
          path={routes.salesOrders.packingList.tab}
          render={routeProps => userCanOrRedirect(['manage_sales_orders'],
            <PackingList {...props} {...routeProps} />)}/>
        <Route
          exact
          path={routes.salesOrders.tab}
          render={routeProps => userCanOrRedirect(['manage_sales_orders'],
            <SalesOrder {...props} {...routeProps} />)}/>
        <Route
          exact
          path={routes.archive.salesOrder.detail}
          render={routeProps => userCanOrRedirect(
            ['manage_sales_orders'], <SalesOrder {...props} {...routeProps} archive={true}/>
          )}/>
        <Route
          exact
          path={routes.archive.salesOrder.packingList.detail}
          render={routeProps => userCanOrRedirect(
            ['manage_sales_orders'], <PackingList {...props} {...routeProps} archive={true}/>
          )}/>
        <Route
          exact
          path={routes.archive.salesOrder.packingList.tab}
          render={routeProps => userCanOrRedirect(
            ['manage_sales_orders'], <PackingList {...props} {...routeProps} archive={true}/>
          )}/>
        <Route
          exact
          path={routes.archive.salesOrder.tab}
          render={routeProps => userCanOrRedirect(
            ['manage_sales_orders'], <SalesOrder {...props} {...routeProps} archive={true}/>
          )}/>

        <Route
          exact
          path={routes.materials.index}
          render={routeProps => userCanOrRedirect(['manage_materials'], <Materials {...props} {...routeProps} />)}/>
        <Route
          exact
          path={routes.materials.detail}
          render={routeProps => userCanOrRedirect(['manage_materials'], <Material {...props} {...routeProps} />)}/>
        <Route
          exact
          path={routes.materials.tab}
          render={routeProps => userCanOrRedirect(['manage_materials'], <Material {...props} {...routeProps} />)}/>
        <Route
          exact
          path={routes.archive.material.detail}
          render={routeProps => userCanOrRedirect(
            ['manage_materials'], <Material {...props} {...routeProps} archive={true}/>
          )}/>
        <Route
          exact
          path={routes.archive.material.tab}
          render={routeProps => userCanOrRedirect(
            ['manage_materials'], <Material {...props} {...routeProps} archive={true}/>
          )}/>

        <Route
          exact
          path={routes.users.index}
          render={routeProps => userCanOrRedirect(['manage_users'], <Users {...props} {...routeProps} />)}/>
        <Route
          exact
          path={routes.users.detail}
          render={routeProps => userCanOrRedirect(['manage_users'], <User {...props} {...routeProps} />)}/>

        <Route
          exact
          path={routes.management}
          render={routeProps => userCanOrRedirect(['change_settings', 'manage_access_keys', 'manage_attributes'],
            <Management {...props} {...routeProps} />)}/>

        <Route
          exact
          path={routes.archive.index}
          render={routeProps => userCanOrRedirect(['manage_contacts'], <Archive {...props} {...routeProps} />)}/>

      </Switch>
    </Authenticated>
  );
};

export default withInboxCount(Admin);
